import { AI_SUBSCRIPTION_TYPES } from 'constants/ai'
import { assertProd } from 'utils/assert-prod'

import { CheckoutMetadata } from './Checkout'

// Create a new transaction
type CreateNewTransactionProps = CheckoutMetadata & {
  uid: string
  email?: string | null
}

// Switch subscription
type SwitchSubscriptionProps = CreateNewTransactionProps

// Notify telegram bot
type NotifyTelegramBotProps = {
  uid: string
  email: string
  subscriptionId: string
  type: string
}

/**
 * Create a new transaction
 * @param metadata - The metadata for the transaction
 * @returns The transaction ID
 * @throws Error if the transaction ID is not returned
 */

export const createNewTransaction = async (metadata: CreateNewTransactionProps): Promise<{ transactionId: string }> => {
  const response = await fetch('/api/subscriptions/create/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(metadata),
  })

  if (!response.ok) {
    throw new Error('Failed to create subscription')
  }

  return await response.json()
}

/**
 * Cancel a subscription
 * @param uid - The ID of the user
 * @returns The success or failure of the operation
 * @throws Error if the subscription is not canceled
 */

export const cancelSubscription = async (uid: string): Promise<{ success: boolean }> => {
  const response = await fetch('/api/subscriptions/cancel/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ uid }),
  })

  if (!response.ok) {
    throw new Error('Failed to cancel subscription')
  }

  return await response.json()
}

/**
 * Switch subscription from monthly to yearly or yearly to monthly
 * @param metadata - The metadata for the transaction
 * @returns Promise<{ success: boolean }>
 */

export const switchSubscription = async (
  metadata: SwitchSubscriptionProps
): Promise<{ success: boolean; transactionId: string }> => {
  const [{ success: cancelSuccess }, { transactionId }] = await Promise.all([
    cancelSubscription(metadata.uid),
    createNewTransaction(metadata),
  ])

  if (!cancelSuccess || !transactionId) {
    assertProd(false, `Failed to switch subscription for user ${metadata.uid}`)
    throw new Error('Failed to switch subscription')
  }

  return { success: true, transactionId }
}

/**
 * Call telegram bot to send new subscription alert to the admin
 * @param uid - The ID of the user
 * @param email - The email of the user
 * @param transactionId - The ID of the transaction
 * @param type - The type of the subscription
 * @returns Promise<void>
 */

export const notifyTelegramBot = async ({
  uid,
  email,
  subscriptionId,
  type,
}: NotifyTelegramBotProps): Promise<void> => {
  const botToken = process.env.NEXT_PUBLIC_TELEGRAM_BOT_TOKEN
  const chatId = process.env.NEXT_PUBLIC_TELEGRAM_CHAT_ID

  if (!botToken || !chatId) {
    assertProd(
      false,
      `Telegram notification skipped for user ${uid} - missing environment variables (TELEGRAM_BOT_TOKEN or TELEGRAM_CHAT_ID)`
    )
    return
  }

  let text = ''

  // constants
  const subscriptionType = type.charAt(0).toUpperCase() + type.slice(1)

  if (type === AI_SUBSCRIPTION_TYPES.CANCELED) {
    // Subscription canceled
    text = `<b>Subscription Canceled Alert!</b>\n\nA user just canceled their subscription to the <b>Typing Mentor AI Coach</b>.\n\n<b>User ID:</b> <code>${uid}</code>\n<b>User Email:</b> <code>${email}</code>\n<b>Subscription ID:</b> <code>${subscriptionId}</code>\n<b>Subscription Type:</b> <code>${subscriptionType}</code>\n\n<i>Please verify the cancellation and ensure access is revoked accordingly.</i>`
  } else if (type === AI_SUBSCRIPTION_TYPES.PAST_DUE) {
    // Subscription past due
    text = `<b>Subscription Past Due Alert!</b>\n\nA user just past due their subscription to the <b>Typing Mentor AI Coach</b>.\n\n<b>User ID:</b> <code>${uid}</code>\n<b>User Email:</b> <code>${email}</code>\n<b>Subscription ID:</b> <code>${subscriptionId}</code>\n<b>Subscription Type:</b> <code>${subscriptionType}</code>\n\n<i>Please verify the past due and contact the user to update their payment method.</i>`
  } else if (type === AI_SUBSCRIPTION_TYPES.TRIAL) {
    // New subscription
    text = `<b>New Subscription Alert!</b>\n\nA user just subscribed to the <b>Typing Mentor AI Coach</b>.\n\n<b>User ID:</b> <code>${uid}</code>\n<b>User Email:</b> <code>${email}</code>\n<b>Subscription ID:</b> <code>${subscriptionId}</code>\n<b>Subscription Type:</b> <code>${subscriptionType}</code>\n\n<i>Please verify payment and ensure access is granted accordingly.</i>`
  } else {
    // Conversion Success after trial
    text = `<b>Subscription Conversion Success Alert!</b>\n\nA user just converted their trial to a paid subscription to the <b>Typing Mentor AI Coach</b>.\n\n<b>User ID:</b> <code>${uid}</code>\n<b>User Email:</b> <code>${email}</code>\n<b>Subscription ID:</b> <code>${subscriptionId}</code>\n<b>Subscription Type:</b> <code>${subscriptionType}</code>\n\n<i>Please verify payment and ensure access is granted accordingly.</i>`
  }

  await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      chat_id: chatId,
      text,
      parse_mode: 'HTML',
    }),
  })
}
